// frontend/src/components/tax/TaxUploadDropzone.tsx
//
// TAX-PARSE-2 — drag/drop + click-to-select PDF upload for tax documents
// (1065, K-1, 1120, 1040). Pattern mirrors the SellerPortal dropzone
// (frontend/src/pages/SellerPortal.tsx).

import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { toast } from 'sonner'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatApiError } from '@/utils/httpErrors'
import { uploadTaxDocument, type TaxDocumentResponse } from '@/api/tax'

const MAX_BYTES = 25 * 1024 * 1024 // 25 MB
const ACCEPT = { 'application/pdf': ['.pdf'] }

const isTouchDevice =
  typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)

const FORM_TYPES = ['1065', '1040', 'k1', '1120'] as const

interface TaxUploadDropzoneProps {
  dealId: string
  onUploaded: (doc: TaxDocumentResponse) => void
}

export function TaxUploadDropzone({ dealId, onUploaded }: TaxUploadDropzoneProps) {
  const [uploading, setUploading] = useState(false)
  const [formType, setFormType] = useState<string>('')
  const [taxYear, setTaxYear] = useState<string>('')

  const onDrop = async (accepted: File[]) => {
    for (const file of accepted) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} exceeds 25 MB limit`)
        continue
      }
      setUploading(true)
      try {
        const doc = await uploadTaxDocument(dealId, file, {
          formType: formType || undefined,
          taxYear: taxYear || undefined,
        })
        onUploaded(doc)
        toast.success(`${file.name} uploaded — ${doc.form_type} extracted`)
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${formatApiError(err)}`)
      } finally {
        setUploading(false)
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxSize: MAX_BYTES,
    disabled: uploading || !dealId,
  })

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Upload Tax Document</CardTitle>
        <p className="text-xs text-muted-foreground">
          1065, K-1, 1120, or 1040 — PDF only, max 25 MB
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="tax-form-type" className="text-xs">
              Form type (optional)
            </Label>
            <select
              id="tax-form-type"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              disabled={uploading}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">Auto-detect</option>
              {FORM_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {ft.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="tax-year" className="text-xs">
              Tax year (optional)
            </Label>
            <Input
              id="tax-year"
              type="number"
              inputMode="numeric"
              placeholder="e.g. 2024"
              value={taxYear}
              onChange={(e) => setTaxYear(e.target.value)}
              disabled={uploading}
            />
          </div>
        </div>

        <div
          {...getRootProps()}
          className={[
            'border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors',
            isDragActive
              ? 'border-accent bg-accent/5'
              : 'border-input hover:border-accent hover:bg-muted',
            uploading || !dealId ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
        >
          <input {...getInputProps()} />
          <p className="text-sm text-muted-foreground">
            {uploading
              ? 'Uploading…'
              : isTouchDevice
                ? 'Tap to select files'
                : isDragActive
                  ? 'Drop file here…'
                  : 'Drag & drop or click to browse'}
          </p>
          <p className="text-[10px] text-muted-foreground/60 mt-1">PDF · max 25 MB</p>
        </div>
      </CardContent>
    </Card>
  )
}

export default TaxUploadDropzone
